import React from "react";
import {
  Users,
  Building2,
  TrendingUp,
  BarChart3,
  ShieldCheck,
  Settings,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getDerivedPaymentStatus } from "../../constants/plans";
import {
  getPlanLabel,
  getRoleBadgeClass,
  getRoleDotClass,
  getRoleLabel,
  getStatusLabel,
} from "../../constants/plans";

const AdminDashboard = ({
  user,
  currentUser,
  adminStats,
  adminUsers,
  navigate,
}) => {
  if (user?.role !== "admin") return null;

  const activationStats = adminUsers.reduce(
    (acc, adminUser) => {
      const status = getDerivedPaymentStatus(adminUser);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    },
    { active: 0, trialing: 0, pending: 0, past_due: 0, canceled: 0, inactive: 0 },
  );

  return (
    <>
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-800 text-white rounded-2xl p-6 sm:p-8 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-slate-300 mb-2">
              Administración
            </p>
            <h2
              className="text-2xl sm:text-3xl font-bold mb-2"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Centro de control de Metal Concrete
            </h2>
            <p className="text-sm text-slate-300 max-w-2xl">
              Supervisa usuarios, activaciones, desarrollos y archivado seguro
              de información desde una sola vista operativa.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate("/admin/users")}>
              Gestionar usuarios
            </Button>
            <Button
              variant="outline"
              className="border-white/20 bg-white/5 text-white hover:bg-white/10"
              onClick={() => navigate("/admin/settings")}
            >
              Configuración
            </Button>
          </div>
        </div>
      </div>

      {/* Panel de Estadisticas del Sistema */}
      {adminStats && (
        <div
          className="bg-background border border-border rounded-lg p-4 sm:p-6 mb-6"
          data-testid="admin-stats-panel"
        >
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-red-600" />
            </div>
            <h2
              className="text-lg sm:text-xl font-bold"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              Panel del sistema
            </h2>
          </div>

          {/* Metricas principales */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-blue-700">
                {adminStats.total_users}
              </p>
              <p className="text-xs sm:text-sm text-blue-600">Usuarios activos</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-green-700">
                {adminStats.total_properties}
              </p>
              <p className="text-xs sm:text-sm text-green-600">Propiedades visibles</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-purple-700">
                {adminStats.paid_users}
              </p>
              <p className="text-xs sm:text-sm text-purple-600">
                Usuarios Pagados
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 text-center">
              <p className="text-2xl sm:text-3xl font-bold text-amber-700">
                {adminStats.total_developments}
              </p>
              <p className="text-xs sm:text-sm text-amber-600">Desarrollos visibles</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-4 h-4 text-green-600" />
                <h3 className="font-semibold text-sm">Suscripciones activas</h3>
              </div>
              <p className="text-2xl font-bold">{activationStats.active}</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-amber-600" />
                <h3 className="font-semibold text-sm">Pendientes</h3>
              </div>
              <p className="text-2xl font-bold">{activationStats.pending}</p>
            </div>
            <div className="bg-muted/30 rounded-lg p-4 border border-border">
              <div className="flex items-center gap-2 mb-2">
                <Settings className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-sm">Sesión</h3>
              </div>
              <p className="text-sm font-medium">{currentUser?.email}</p>
            </div>
          </div>

          {/* Distribucion por Rol y Plan */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-muted/30 rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Users className="w-4 h-4" /> Por Rol
              </h3>
              {adminStats.users_by_role &&
                Object.entries(adminStats.users_by_role).map(
                  ([role, count]) => (
                    <div
                      key={role}
                      className="flex items-center justify-between mb-2"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-3 h-3 rounded-full ${getRoleDotClass(role)}`}
                        />
                        <span className="text-sm">{getRoleLabel(role)}</span>
                      </div>
                      <span className="text-sm font-bold">{count}</span>
                    </div>
                  ),
                )}
            </div>
            <div className="bg-muted/30 rounded-lg p-4">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" /> Activación
              </h3>
              {Object.entries(activationStats).map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center justify-between mb-2"
                >
                  <span className="text-sm">{getStatusLabel(status)}</span>
                  <span className="text-sm font-bold">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Usuarios recientes */}
          {adminUsers.length > 0 && (
            <div className="bg-muted/30 rounded-lg p-4 max-h-[400px] overflow-y-auto">
              <h3 className="font-semibold mb-4">Usuarios recientes</h3>
              <div className="space-y-2">
                {adminUsers.slice(0, 10).map((u, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-2 bg-background rounded border border-border"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {u.email}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${getRoleBadgeClass(u.role)}`}
                      >
                        {getRoleLabel(u.role)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {getPlanLabel(u.subscription_plan)} ·{" "}
                        {getStatusLabel(getDerivedPaymentStatus(u))}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export { AdminDashboard };
